/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

import React, { Suspense } from "react";
import { ThemeProvider } from "./src/theme/ThemeProvider";
import { I18nProvider } from "./src/i18n/I18nProvider";
const RetroStatusBar = React.lazy(() => import("./src/components/RetroPageLoader"));

const DEBUG_90S_LOADING = false;

function debugLog(...args) {
  if (DEBUG_90S_LOADING) console.log("[90s-debug]", ...args);
}
// eslint-disable-next-line
export function shouldUpdateScroll() {
  if (localStorage.getItem("90sSimulator") === "true") {
    return false;
  }

  const body = document.getElementsByTagName("body")[0];
  const tableOfContents = document.getElementsByClassName("table-of-contents");

  if (tableOfContents.length > 0) {
    window.scrollTo(0, 0);
    body.scrollTop = 0;
    return false;
  } else {
    return true;
  }
}

function cleanupExistingOverlays() {
  // Remove any leftover overlays from previous page
  document
    .querySelectorAll('[id^="retro-border-overlay-"]')
    .forEach((el) => el.remove());
  document
    .querySelectorAll('[id^="retro-icon-overlay-"]')
    .forEach((el) => el.remove());
  document.getElementById("retro-animation-style")?.remove();
  document.getElementById("netscape-page-snapshot")?.remove();
  document.getElementById("netscape-hide-page-style")?.remove();

  // Remove reveal phase classes from previous page
  const pageContent = document.getElementById("page-content");
  const revealTarget = pageContent
    ? pageContent.querySelector(":scope > .retro-page-body")
    : null;
  if (revealTarget) {
    revealTarget.classList.remove(
      "retro-reveal-phase-1",
      "retro-reveal-phase-2",
      "retro-reveal-phase-3",
    );
  }
}

export function onPreRouteUpdate({ location, prevLocation }) {
  if (
    window.__netscapeStatusBar &&
    localStorage.getItem("90sSimulator") === "true"
  ) {
    if (prevLocation && location.pathname === prevLocation.pathname) return;

    debugLog("onPreRouteUpdate fired");
    const pageContent = document.getElementById("page-content");
    const revealTarget = pageContent
      ? pageContent.querySelector(":scope > .retro-page-body")
      : null;

    const savedScrollTop = pageContent ? pageContent.scrollTop : 0;

    // Remove any snapshot from a previous interrupted navigation
    document.getElementById("netscape-page-snapshot")?.remove();

    if (revealTarget) {
      // Remove old phases, then set Phase 1
      revealTarget.classList.remove(
        "retro-reveal-phase-1",
        "retro-reveal-phase-2",
        "retro-reveal-phase-3",
      );
      revealTarget.classList.add("retro-reveal-phase-1");
    }

    // Clone overlay elements (borders, icons) with their current opacity
    // before removing them, so they appear in the snapshot unchanged.
    // Overlays are inside .retro-page-body so their positions are
    // content-relative (no scroll offset adjustment needed).
    const liveOverlays = [];
    const borderOverlays = document.querySelectorAll(
      '[id^="retro-border-overlay-"]',
    );
    const iconOverlays = document.querySelectorAll(
      '[id^="retro-icon-overlay-"]',
    );
    borderOverlays.forEach((el) => {
      const clone = el.cloneNode(true);
      clone.style.setProperty(
        "opacity",
        window.getComputedStyle(el).opacity,
        "important",
      );
      liveOverlays.push(clone);
    });
    iconOverlays.forEach((el) => {
      const clone = el.cloneNode(true);
      clone.style.setProperty(
        "opacity",
        window.getComputedStyle(el).opacity,
        "important",
      );
      liveOverlays.push(clone);
    });

    // Remove overlays from DOM before capturing snapshot so they aren't
    // duplicated (they're inside .retro-page-body which gets innerHTML'd)
    document
      .querySelectorAll('[id^="retro-border-overlay-"]')
      .forEach((el) => el.remove());
    document
      .querySelectorAll('[id^="retro-icon-overlay-"]')
      .forEach((el) => el.remove());

    // Only capture a snapshot if the page is ready (language resolved).
    // If not-ready, the page is intentionally hidden (opacity: 0) because
    // a redirect is pending — showing a snapshot of wrong-language content
    // would defeat the purpose of the not-ready mechanism.
    if (pageContent && !pageContent.classList.contains("not-ready")) {
      // Capture image visual states while animation CSS is still active
      const images = Array.from(pageContent.querySelectorAll("img"));
      const imageStates = images.map((img) => {
        const computed = window.getComputedStyle(img);
        return {
          clipPath: computed.clipPath,
          opacity: computed.opacity,
        };
      });

      // Save snapshot (overlays already removed so no duplicates)
      window.__netscapePageSnapshot = pageContent.innerHTML;
      window.__netscapePageSnapshotClassName = pageContent.className;
      window.__netscapePageSnapshotImageStates = imageStates;
    }

    // Now that snapshot and image states are captured, remove animation CSS
    // and hide-page style so they don't interfere with the new page
    document.getElementById("retro-animation-style")?.remove();
    document.getElementById("netscape-hide-page-style")?.remove();

    // Style tag survives React re-renders that might strip the Phase 1 class
    let hideStyle = document.getElementById("netscape-hide-page-style");
    if (!hideStyle) {
      hideStyle = document.createElement("style");
      hideStyle.id = "netscape-hide-page-style";
      document.head.appendChild(hideStyle);
    }
    hideStyle.textContent =
      "body.retro-theme #page-content > .retro-page-body { clip-path: inset(0 0 100% 0) !important; visibility: hidden !important; }";

    document.body.classList.add("retro-theme");

    // Hide images on the real page (not the snapshot)
    let style = document.getElementById("netscape-hide-images-style");
    if (!style) {
      style = document.createElement("style");
      style.id = "netscape-hide-images-style";
      document.head.appendChild(style);
    }
    style.textContent = `
      #page-content img,
      #page-content .gatsby-resp-image-background-image,
      #page-content .gatsby-image-wrapper [data-placeholder-image],
      #page-content .gatsby-image-wrapper-placeholder {
        opacity: 0 !important;
        transition: none !important;
      }
    `;

    // Also set inline opacity:0 on images that had inline opacity:1 from
    // the previous page's animation, since inline !important beats
    // stylesheet !important in the CSS cascade.
    pageContent.querySelectorAll("img").forEach((img) => {
      img.style.setProperty("opacity", "0", "important");
      img.style.setProperty("transition", "none", "important");
    });
    pageContent
      .querySelectorAll(
        ".gatsby-resp-image-background-image, .gatsby-image-wrapper [data-placeholder-image], .gatsby-image-wrapper-placeholder",
      )
      .forEach((el) => {
        el.style.setProperty("opacity", "0", "important");
        el.style.setProperty("transition", "none", "important");
      });

    // Show snapshot overlay with frozen image states and cloned overlays
    if (window.__netscapePageSnapshot) {
      const overlay = document.createElement("div");
      overlay.id = "netscape-page-snapshot";
      overlay.innerHTML = window.__netscapePageSnapshot;
      overlay.className = window.__netscapePageSnapshotClassName || "";
      const pageContentStyle = pageContent
        ? window.getComputedStyle(pageContent)
        : null;
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: calc(100vh - 26px);
        z-index: 9998;
        overflow: auto;
        scrollbar-gutter: stable;
        background-color: transparent;
        background-image: none;
      `;
      document.body.appendChild(overlay);

      // Freeze each image at its current visual state so partially-loaded
      // images stay partially-loaded instead of becoming fully visible
      const imageStates = window.__netscapePageSnapshotImageStates;
      if (imageStates) {
        const overlayImages = overlay.querySelectorAll("img");
        overlayImages.forEach((img, i) => {
          if (i < imageStates.length) {
            // Remove animation classes so CSS animations don't restart
            img.className = img.className.replace(/retro-img-\d+/g, "").trim();
            // Freeze at current clip-path state
            if (imageStates[i].clipPath && imageStates[i].clipPath !== "none") {
              img.style.setProperty(
                "clip-path",
                imageStates[i].clipPath,
                "important",
              );
            }
            // Freeze at current opacity
            img.style.setProperty(
              "opacity",
              imageStates[i].opacity,
              "important",
            );
            img.style.setProperty("transition", "none", "important");
            // Preserve retro filter on snapshot images
            if (!img.src.endsWith(".gif")) {
              img.style.setProperty("image-rendering", "crisp-edges", "important");
              img.style.setProperty("filter", "blur(0.3px) contrast(0.92) saturate(0.9)", "important");
            }
          }
        });
      }

      // Append cloned overlay elements inside .retro-page-body in the snapshot
      // so they are positioned relative to the same containing block as on the live page
      const snapshotBody = overlay.querySelector(".retro-page-body") || overlay;
      liveOverlays.forEach((clone) => snapshotBody.appendChild(clone));

      // Neutralize gatsby-remark-images inside the snapshot so its browser
      // script (which runs on every route update and queries all
      // .gatsby-resp-image-wrapper elements in the document) doesn't strip
      // !important flags from inline styles or modify opacity/transition.
      overlay.querySelectorAll(".gatsby-resp-image-wrapper").forEach((wrapper) => {
        wrapper.classList.replace("gatsby-resp-image-wrapper", "snapshot-gatsby-resp-image-wrapper");
      });
      overlay.querySelectorAll(".gatsby-resp-image-background-image").forEach((bg) => {
        bg.classList.replace("gatsby-resp-image-background-image", "snapshot-gatsby-resp-image-background-image");
      });
      overlay.querySelectorAll(".gatsby-resp-image-image").forEach((img) => {
        img.classList.replace("gatsby-resp-image-image", "snapshot-gatsby-resp-image-image");
      });

      overlay.scrollTop = savedScrollTop;
      delete window.__netscapePageSnapshot;
      delete window.__netscapePageSnapshotClassName;
      delete window.__netscapePageSnapshotImageStates;
    }

    window.__netscapeStatusBar.startLoading();
  }
}

export function onRouteUpdate({ location, prevLocation }) {
  if (localStorage.getItem("90sSimulator") === "true") {
    if (prevLocation && location.pathname === prevLocation.pathname) return;

    debugLog("onRouteUpdate fired, initialLoadDone:", window.__netscapeStatusBarInitialLoadDone);
    if (!window.__netscapeStatusBarInitialLoadDone) {
      window.__netscapeStatusBarInitialLoadDone = true;
      return;
    }
    if (window.__netscapeStatusBar) {
      // Ensure wallpaper is visible immediately during navigation
      const pageContent = document.getElementById("page-content");
      if (pageContent) {
        pageContent.classList.add("wallpaper-ready");
      }
      // Re-ensure Phase 1 in case React stripped the class during re-render
      const revealTarget = pageContent
        ? pageContent.querySelector(":scope > .retro-page-body")
        : null;
      if (revealTarget) {
        revealTarget.classList.add("retro-reveal-phase-1");
      }

      window.__netscapeStatusBar.finishLoading();
    }

    // If loading is already complete, swap any still GIFs that
    // appeared during navigation (e.g. React-hydrated footer GIFs)
    if (document.body.classList.contains("retro-gif-animating")) {
      document.querySelectorAll("img[data-animated-src]").forEach((img) => {
        const animatedSrc = img.getAttribute("data-animated-src");
        if (animatedSrc) {
          img.src = animatedSrc;
        }
        img.removeAttribute("data-animated-src");
      });
    }
  }
}

export const wrapRootElement = ({ element }) => (
  <ThemeProvider>
    <I18nProvider>
      {element}
      {typeof window !== 'undefined' && localStorage.getItem('90sSimulator') === 'true' && (
        <Suspense fallback={null}>
          <RetroStatusBar />
        </Suspense>
      )}
    </I18nProvider>
  </ThemeProvider>
);
